<script setup lang="ts">
import { ref, inject, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Dayjs } from 'dayjs';
import { useI18n } from 'vue-i18n';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';

import { useBookingViewStore } from '@/stores/booking-view-store';
import { useUserStore } from '@/stores/user-store';
import { Slot } from '@/models';
import { dayjsKey } from '@/keys';
import { getStartOfWeek, timeFormat } from '@/utils';

import SlotSelectionAside from './SlotSelectionAside.vue';
import SlotSelectionHeader from './SlotSelectionHeader.vue';

const { t } = useI18n();
const userStore = useUserStore();
const { appointment, activeDate, selectedEvent } = storeToRefs(useBookingViewStore());
const dj = inject(dayjsKey);

const visibleMonth = ref(dj());

const slotsByDate = computed(() => {
  const map: Record<string, Slot[]> = {};
  if (!appointment.value?.slots) return map;

  appointment.value.slots.forEach((slot) => {
    const key = dj(slot.start).format('YYYY-MM-DD');
    if (!map[key]) {
      map[key] = [];
    }
    map[key].push(slot);
  });

  Object.values(map).forEach((slots) => {
    slots.sort((a, b) => dj(a.start).valueOf() - dj(b.start).valueOf());
  });

  return map;
});

const availableDates = computed(() => new Set(Object.keys(slotsByDate.value)));

const minSlotDate = computed(() => {
  const dates = Object.keys(slotsByDate.value);
  if (!dates.length) return dj().format('YYYY-MM-DD');
  return dates.reduce((min, current) => (current < min ? current : min), dates[0]);
});

const maxSlotDate = computed(() => {
  const dates = Object.keys(slotsByDate.value);
  if (!dates.length) return dj().format('YYYY-MM-DD');
  return dates.reduce((max, current) => (current > max ? current : max), dates[0]);
});

const minMonth = computed(() => dj(minSlotDate.value).startOf('month'));
const maxMonth = computed(() => dj(maxSlotDate.value).startOf('month'));

const canGoPrev = computed(() => visibleMonth.value.isAfter(minMonth.value, 'month'));
const canGoNext = computed(() => visibleMonth.value.isBefore(maxMonth.value, 'month'));

const weekdayLabels = computed(() => {
  const startOfWeek = userStore.data.settings.startOfWeek ?? 7;
  const labels = [];
  const start = getStartOfWeek(dj(), startOfWeek);
  for (let i = 0; i < 7; i += 1) {
    labels.push(start.add(i, 'day').format('dd').toUpperCase());
  }
  return labels;
});

const calendarDays = computed(() => {
  const startOfWeek = userStore.data.settings.startOfWeek ?? 7;
  const monthStart = visibleMonth.value.startOf('month');
  const gridStart = getStartOfWeek(monthStart, startOfWeek);
  return Array.from({ length: 42 }).map((_, index) => {
    const date = gridStart.add(index, 'day');
    const key = date.format('YYYY-MM-DD');
    return {
      date,
      key,
      isCurrentMonth: date.month() === visibleMonth.value.month(),
      isAvailable: availableDates.value.has(key),
      isSelected: date.isSame(activeDate.value, 'day'),
      isToday: date.isSame(dj(), 'day'),
      slotsCount: slotsByDate.value[key]?.length ?? 0,
    };
  });
});

const selectedDateKey = computed(() => activeDate.value.format('YYYY-MM-DD'));
const selectedSlots = computed(() => slotsByDate.value[selectedDateKey.value] ?? []);
const selectedDateLabel = computed(() => activeDate.value.format('dddd, D MMMM'));
const timezone = computed(() => dj.tz.guess());

const selectDate = (date: Dayjs) => {
  activeDate.value = date;
};

const selectSlot = (slot: Slot) => {
  if (!appointment.value) return;
  appointment.value.slots.forEach((item) => {
    item.selected = item === slot;
  });
  const event = { ...appointment.value, ...slot } as any;
  delete event.slots;
  selectedEvent.value = event;
};

const goPrevMonth = () => {
  if (!canGoPrev.value) return;
  visibleMonth.value = visibleMonth.value.subtract(1, 'month');
  const firstAvailable = calendarDays.value.find((day) => day.isAvailable && day.isCurrentMonth);
  if (firstAvailable) {
    selectDate(firstAvailable.date);
  }
};

const goNextMonth = () => {
  if (!canGoNext.value) return;
  visibleMonth.value = visibleMonth.value.add(1, 'month');
  const firstAvailable = calendarDays.value.find((day) => day.isAvailable && day.isCurrentMonth);
  if (firstAvailable) {
    selectDate(firstAvailable.date);
  }
};

watch(() => activeDate.value, (value) => {
  if (!value) return;
  if (!value.isSame(visibleMonth.value, 'month')) {
    visibleMonth.value = value.startOf('month');
  }
}, { immediate: true });
</script>

<template>
  <template v-if="appointment">
    <div class="booking-layout">
      <section class="booking-card booking-card--info">
        <slot-selection-header />
      </section>

      <section class="booking-card booking-card--calendar">
        <div class="month-header">
          <button class="ghost" :disabled="!canGoPrev" @click="goPrevMonth">
            <ph-caret-left size="20" />
          </button>
          <div>
            <p class="month-label">{{ visibleMonth.format('MMMM YYYY') }}</p>
            <p class="month-subtitle">{{ t('label.timeZone') }}: {{ timezone }}</p>
          </div>
          <button class="ghost" :disabled="!canGoNext" @click="goNextMonth">
            <ph-caret-right size="20" />
          </button>
        </div>

        <div class="month-grid">
          <div v-for="weekday in weekdayLabels" :key="weekday" class="month-weekday">
            {{ weekday }}
          </div>
          <button
            v-for="day in calendarDays"
            :key="day.key"
            :class="[
              'month-day',
              { outside: !day.isCurrentMonth, available: day.isAvailable, selected: day.isSelected, today: day.isToday }
            ]"
            :disabled="!day.isAvailable"
            @click="selectDate(day.date)"
          >
            <span>{{ day.date.date() }}</span>
            <span v-if="day.slotsCount" class="slots-count">{{ day.slotsCount }}</span>
          </button>
        </div>
      </section>

      <section class="booking-card booking-card--slots">
        <div class="slots-header">
          <div>
            <h3>{{ t('label.availableTimes') }}</h3>
            <p class="muted">{{ selectedDateLabel }}</p>
          </div>
        </div>

        <div v-if="selectedSlots.length" class="slots-grid">
          <button
            v-for="slot in selectedSlots"
            :key="String(slot.start)"
            :class="['slot-pill', { active: slot.selected }]"
            @click="selectSlot(slot)"
          >
            {{ dj(slot.start).format(timeFormat()) }}
          </button>
        </div>
        <div v-else class="slots-empty">
          {{ t('text.noTimesAvailable') }}
        </div>

        <div class="slots-divider"></div>

        <slot-selection-aside />
      </section>
    </div>
  </template>
</template>

<style scoped>
@import '@/assets/styles/custom-media.pcss';

.booking-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

.booking-card {
  background: var(--colour-neutral-base);
  border: 1px solid var(--colour-neutral-border);
  border-radius: 1.25rem;
  padding: 1.25rem;
  box-shadow: 0 18px 40px rgba(11, 16, 36, 0.08);
}

.booking-card--calendar {
  background: linear-gradient(180deg, var(--colour-neutral-base), var(--colour-neutral-lower));
}

.booking-card--info {
  background: linear-gradient(180deg, rgba(20, 26, 58, 0.08), transparent 80%);
}

.month-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.month-label {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--colour-ti-base);
}

.month-subtitle {
  font-size: 0.75rem;
  color: var(--colour-ti-secondary);
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.5rem;
}

.month-weekday {
  text-align: center;
  font-size: 0.7rem;
  color: var(--colour-ti-muted);
  letter-spacing: 0.06em;
}

.month-day {
  position: relative;
  height: 52px;
  border-radius: 12px;
  border: 1px solid transparent;
  background: var(--colour-neutral-lower);
  color: var(--colour-ti-secondary);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}

.month-day.available {
  background: var(--colour-neutral-base);
  border-color: var(--colour-neutral-border);
  color: var(--colour-ti-base);
}

.month-day.selected {
  background: var(--colour-primary-default);
  color: #ffffff;
  border-color: var(--colour-primary-default);
}

.month-day.today {
  box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--colour-primary-default) 50%, transparent);
}

.month-day.outside {
  opacity: 0.35;
}

.month-day:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.slots-count {
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 0.65rem;
  background: var(--colour-primary-soft);
  color: var(--colour-ti-highlight);
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
}

.slots-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.slots-header h3 {
  font-size: 1.1rem;
  color: var(--colour-ti-base);
}

.slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 0.75rem;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.slot-pill {
  border: 1px solid var(--colour-neutral-border);
  border-radius: 999px;
  padding: 0.55rem 0.75rem;
  background: var(--colour-neutral-base);
  color: var(--colour-ti-base);
  font-weight: 600;
}

.slot-pill.active {
  background: var(--colour-primary-default);
  color: white;
  border-color: var(--colour-primary-default);
}

.slots-empty {
  font-size: 0.9rem;
  color: var(--colour-ti-secondary);
  padding: 0.75rem 0;
}

.slots-divider {
  margin: 1.5rem 0;
  height: 1px;
  background: var(--colour-neutral-border);
}

.ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid var(--colour-neutral-border);
  padding: 0.35rem;
  background: var(--colour-neutral-base);
  color: var(--colour-ti-secondary);
}

.booking-card--slots :deep(aside) {
  border: none;
  padding: 0;
  background: transparent;
}

@media (--lg) {
  .booking-layout {
    grid-template-columns: minmax(240px, 1fr) minmax(320px, 1.4fr) minmax(300px, 1fr);
    align-items: start;
  }
}
</style>
